import { ClientJoinedData, GameFileConfig, PreConfigData, LEAVE_AREA_REASONS, ReceivedMessage } from "@gotti/core";
import type { AreaServer } from "./servers/AreaServer.js";

/**
 * Client data passed to area join request handlers.
 */
export type JoinAreaRequestClientData<Auth={}, Options={}> = ClientJoinedData<Auth, Options> & ({
    fromClient: true,
    fromArea: false,
} | {
    fromClient: false,
    fromArea: true,
    areaId: string,
    areaData: any,
})

/**
 * API passed to area file hooks.
 */
export type AreaFileApi = {
    requestMaster: (message: { type: string, data: any }) => Promise<any>,
    requestArea: (message: { type: string, data: any }) => Promise<any>,
}

/**
 * Game data after server config is applied.
 */
export type PostConfigGameData = PreConfigData & { data: { server: any, client: any }}

type MaybePromise<T>  = Promise<T> | T;
/**
 * Data resolver for server/client area payloads.
 */
export type ServerEntryDataResolver<ServerDataType=any, ClientDataType=any> = {
  server: Partial<ServerDataType> | ((configData: { game?: PostConfigGameData, area: PreConfigData}) => MaybePromise<Partial<ServerDataType>>)
  // client will get copy of this data and accessible through system.$areaData[areaId] since a client can be in multiple areas.
  client: Partial<ClientDataType> | ((configData: { game?: PostConfigGameData, area: PreConfigData}) => MaybePromise<Partial<ClientDataType>>)
  shared: Partial<ServerDataType> & Partial<ClientDataType> | ((configData: { game?: PostConfigGameData, area: PreConfigData}) => MaybePromise<Partial<ServerDataType> & Partial<ClientDataType>>)
} | {
  server: ServerDataType | ((configData: { game?: PostConfigGameData, area: PreConfigData}) => MaybePromise<ServerDataType>)
  // client will get copy of this data and accessible through system.$areaData[areaId] since a client can be in multiple areas.
  client: ClientDataType | ((configData: { game?: PostConfigGameData, area: PreConfigData}) => MaybePromise<ClientDataType>)
} | {
  // client will get copy of this data and accessible through system.$areaData[areaId] since a client can be in multiple areas.
  client: Partial<ClientDataType> | ((configData: { game?: PostConfigGameData, area: PreConfigData}) => MaybePromise<Partial<ClientDataType>>)
  shared: ServerDataType & Partial<ClientDataType> | ((configData: { game?: PostConfigGameData, area: PreConfigData}) => MaybePromise<ServerDataType & Partial<ClientDataType>>)
} | {
  server: Partial<ServerDataType> | ((configData: { game?: PostConfigGameData, area: PreConfigData}) => MaybePromise<Partial<ServerDataType>>)
  shared: ClientDataType & Partial<ServerDataType> | ((configData: { game?: PostConfigGameData, area: PreConfigData}) => MaybePromise<ClientDataType & Partial<ServerDataType>>)
} |{
  server: ServerDataType | ((configData: { game?: PostConfigGameData, area: PreConfigData}) => MaybePromise<ServerDataType>)
} | {
  shared: ServerDataType & ClientDataType | ((configData: { game?: PostConfigGameData, area: PreConfigData}) => MaybePromise<ServerDataType & ClientDataType>)
} | {
  client: ClientDataType | ((configData: { game?: PostConfigGameData, area: PreConfigData}) => MaybePromise<ClientDataType>)
}

/**
 * Whether this area holds a disconnected client's seat for a possible rejoin,
 * and for how long. Declared on the AREA FILE because it is a gameplay property
 * of what the area IS — a live match holds a seat because the team needs that
 * player back; a lobby has nothing worth holding. Resolution: the area file's
 * policy REPLACES the game file's, the game file supplies the default, absent
 * means OFF — so an area can turn reconnect back off. (Not the
 * `preventMultiClientAreas` rule, which is `gameFile || areaFile`: either-true
 * wins there and an area cannot opt out.)
 *
 * `enabled: false` and `graceMs <= 0` both mean off. There is no separate
 * kill-switch: an area either holds seats or it does not.
 */
export type ReconnectPolicy = { enabled: boolean, graceMs: number };

/**
 * Server-side area definition.
 *
 * @template TApi - Shape of the object returned by the `api` method.
 *   When provided, the same keys **must** be declared in
 *   `ServerApiRegistry` via `declare module '@gotti/server'`.
 * @template ServerDataType - Shape of the server-side area data.
 * @template ClientDataType - Shape of the client-side area data.
 */
export type ServerAreaFile<TApi = {}, ServerDataType=any,  ClientDataType=any> = GameFileConfig & {
    // set this to true if you want to have a single instance of this area across all games and clients
    globalSingleton?: boolean,
    // set this to true if you dont want the client to be able to join this area, the area wont even be available in any of the gate resolver function options
    serverOnly?: boolean,
    // set this to true if you dont have an onJoinRequest handler and you want to allow all joins
    allowAllJoins?: boolean,
    // can override preventClientAreaLimit from the game file if you want this area to
    // make sure its the only one the client is joined in.
    preventMultiClientAreas?: boolean,
    /** Hold this area's seats for a reconnecting client — see ReconnectPolicy. */
    reconnect?: ReconnectPolicy,
  // Simulation timestep (in seconds). If provided, overrides tickRate.
    timeStep?: number,
  // If true, the server uses a fixed timestep update (deterministic stepping).
  // If false/omitted, server uses variable timestep (current behavior).
    fixedTimeStep?: boolean,
    /** Asset paths preloaded (into $assets) before this file's systems start. */
    preloads?: { client?: string[]; server?: string[] },
    onJoinRequest?(server: AreaServer, client: JoinAreaRequestClientData, areaStateData?: any) : Promise<any> | any,
    api?: TApi | ((data?: ServerDataType) => TApi | Promise<TApi>),
    data?: ServerEntryDataResolver<ServerDataType, ClientDataType> 
    | ((configData: { game?: PostConfigGameData, area: PreConfigData }) => MaybePromise<{ server?: ServerDataType, client?: ClientDataType }>)
    onClientGameMessage?(server: AreaServer, clientId: string, message: ReceivedMessage) : void,
    onAreaGameMessage?(server: AreaServer, areaId: string, message: ReceivedMessage) : void,
    onClientGameRequest?(server: AreaServer, clientId: string, message: ReceivedMessage) : any | Promise<any>,
    onAreaGameRequest?(server: AreaServer, clientId: string, message: ReceivedMessage) : any | Promise<any>,
    onGlobalGameRequest?(server: AreaServer, message: ReceivedMessage) : any | Promise<any>,
    onGlobalMessage?(message: ReceivedMessage) : void,
    onUpdate?(server: AreaServer, deltaTime: number) : void,
    onBeforeUpdate?: (server: AreaServer, delta: number) => void,
    onAfterUpdate?: (server: AreaServer, delta: number) => void,
    onClientJoined?(server: AreaServer, data: ClientJoinedData) : void,
    onClientLeft?(server: AreaServer, clientId: string, reason: LEAVE_AREA_REASONS, options: any) : void,
    /**
     * Connection-level hook bracketing `onClientJoined`: fires on EVERY
     * connect, both a first join (`joining: true`, immediately followed by
     * `onClientJoined`) and a rebind after a connector-loss detach
     * (`joining: false` — the client was already a member, so `onClientJoined`
     * does NOT re-fire). There is deliberately no separate `onClientReconnected`
     * — that is exactly this hook with `joining: false`. See the "Lifecycle
     * hook contract" in the connector-failover plan.
     */
    onClientConnected?(server: AreaServer, data: {
        // `string | number`, matching the `ClientJoinedData.id` this hook
        // brackets: it is the raw wire publicId, and `useSequentialPublicIds`
        // mints NUMBERS. It was declared `string` here while `onClientJoined`
        // (same value, same call site) already declared `string | number`.
        id: string | number, auth: any, options: any,
        joining: boolean,
        connectorId: string | number,
    }) : void,
    /**
     * Connection-level hook bracketing `onClientLeft`: fires exactly once per
     * disconnect episode. `leaving: true` = deliberate/absolute removal,
     * immediately followed by `onClientLeft` with the same reason. `leaving:
     * false` = the connector died; the client is DETACHED (state held for
     * `reconnectTimeout` ms) — `onClientLeft` does not fire until the grace
     * window expires with no rejoin (or fires never, if it rejoins in time).
     */
    onClientDisconnected?(server: AreaServer, clientId: string, data: {
        leaving: boolean,
        reason: LEAVE_AREA_REASONS,
        reconnectTimeout?: number,
    }) : void,
    // Called when a dynamic area is being drained before teardown, so game code
    // can migrate/kick players or persist state. Awaited before the area closes.
    onDrain?(server: AreaServer, reason?: string) : Promise<void> | void,
    // Fired on every live area when a dynamic area of the same game appears
    // (`onAreaAdded`) or disappears (`onAreaRemoved`) cluster-wide — so peer /
    // matchmaker areas can react. Cheap; safe to omit.
    onAreaAdded?(server: AreaServer, info: { areaId: string, type: string, gameId?: string }) : void,
    onAreaRemoved?(server: AreaServer, info: { areaId: string, type: string, gameId?: string, reason?: string }) : void,
    /**
     * Declarative PER-INSTANCE state, resolved at boot with the same factory
     * params as `data` (`{ area: { id, type, configData }, game?, $assets }`).
     * Where `data` is the per-TYPE payload every instance of this file shares
     * conceptually, `state` seeds this instance's live `areaState`: the
     * `server`/`shared` result becomes `$areaState` (mutable via
     * `setState`/`updateState`), the `client`/`shared` result is what clients
     * receive on join (`$areaState[areaId]` client-side) and on state syncs.
     * Dynamic instances get their identity here — `area.configData` carries the
     * `createArea(type, { configData })` payload.
     */
    state?: ServerEntryDataResolver<any, any>
    | ((configData: { game?: PostConfigGameData, area: PreConfigData }) => MaybePromise<{ server?: any, client?: any }>)
}

/**
 * Area-file keys read ONLY when the area starts (its data/state factories, api
 * factory, asset preloads, loop timing, debug system, singleton placement). A
 * live area file replacement (`AreaServer.replaceAreaFile`) cannot apply a
 * change to them — it reports which changed, and they take effect the next time
 * the area starts. Every other key (join/message/request hooks, update hooks,
 * `allowAllJoins`, `serverOnly`, `reconnect`, …) is read live and applies at once.
 */
export const AREA_START_ONLY_KEYS = ['api', 'data', 'state', 'preloads', 'timeStep', 'fixedTimeStep', 'debug', 'globalSingleton'] as const;

/** Game-file keys read only when an area starts (see `AreaServer.reloadGameFile`). */
export const GAME_START_ONLY_KEYS = ['api', 'data', 'preloads', 'timeStep', 'fixedTimeStep', 'debug', 'meta', 'authIdentityPath'] as const;

/** Owner key the area file's systems are added under (`Process.systemOwners`); the game file's use `GAME_FILE_SYSTEM_OWNER`. */
export const AREA_FILE_SYSTEM_OWNER = '__area__';

/**
 * Deterministic text of a value for change detection, functions included (by
 * source) — `JSON.stringify` drops functions, and a re-evaluated module hands
 * back NEW function objects whose source may be identical.
 */
export function stableValueText(v: unknown, seen: Set<unknown> = new Set()): string {
  if (typeof v === 'function') return `fn:${Function.prototype.toString.call(v)}`;
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? String(v);
  if (seen.has(v)) return '[circular]';
  seen.add(v);
  try {
    if (Array.isArray(v)) return `[${v.map((x) => stableValueText(x, seen)).join(',')}]`;
    return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${stableValueText((v as Record<string, unknown>)[k], seen)}`).join(',')}}`;
  } finally {
    seen.delete(v);
  }
}

/** Which of `keys` differ between two files (by `stableValueText`). */
export function changedKeys(prev: object, next: object, keys: readonly string[]): string[] {
  return keys.filter((k) => stableValueText((prev as Record<string, unknown>)[k]) !== stableValueText((next as Record<string, unknown>)[k]));
}
